import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillWave, faSpinner, faEdit } from '@fortawesome/free-solid-svg-icons';
import api from '../../../Api';

// Les mêmes valeurs que PaymentRelanceHelper::MODES côté serveur.
export const RELANCE_MODES = [
  { value: '30', label: '30 jours' },
  { value: '60', label: '60 jours' },
  { value: '90', label: '90 jours' },
  { value: '120', label: '120 jours' },
  { value: 'reception', label: 'À la réception' },
];

export const DEFAULT_RELANCE_MODE = '60';

export function relanceEndpoint(projectId, slotId) {
  return slotId
    ? `/projects/${projectId}/slots/${slotId}/payment-relance`
    : `/projects/${projectId}/payment-relance`;
}

export function savePaymentRelance(projectId, slotId, mode) {
  return api.put(relanceEndpoint(projectId, slotId), { mode });
}

const fmt = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');

const NEXT_ACTION_LABELS = {
  relance: 'Prochaine relance',
  relance_2: 'Seconde relance',
  escalation: 'Prochaine escalade',
};

// Même action, mais la date est déjà passée : elle est due, pas « prochaine ».
const LATE_ACTION_LABELS = {
  relance: 'Relance due depuis',
  relance_2: 'Seconde relance due depuis',
  escalation: 'Escalade due depuis',
};

/**
 * Délai de paiement accordé au client, pour un projet ou une enquête en closing.
 *
 * L'affichage vient entièrement de `state` (calculé côté serveur) : aucune copie locale, sinon
 * une modale déjà ouverte sur laquelle on change de carte garderait les valeurs de la précédente.
 * Seul le brouillon de modification est local, et il est réinitialisé dès que la carte change
 * d'élément.
 */
const PaymentRelanceCard = ({ projectId, slotId = null, state, canEdit = false, onSaved }) => {
  const entityKey = `${projectId}:${slotId || 'project'}:${state?.mode ?? 'default'}`;

  const [draft, setDraft] = useState(state?.mode || DEFAULT_RELANCE_MODE);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [loadedKey, setLoadedKey] = useState(entityKey);

  // Changement d'élément (ou de valeur enregistrée) : on repart de l'état du serveur.
  if (loadedKey !== entityKey) {
    setLoadedKey(entityKey);
    setDraft(state?.mode || DEFAULT_RELANCE_MODE);
    setEditing(false);
    setError(null);
  }

  if (!state) return null;

  const overdue = (state.days_overdue || 0) > 0;
  const bg = overdue ? '#fef2f2' : '#eff6ff';
  const border = overdue ? '#fecaca' : '#bfdbfe';
  const accent = overdue ? '#dc2626' : '#1d4ed8';
  const titleColor = overdue ? '#991b1b' : '#1e40af';

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await savePaymentRelance(projectId, slotId, draft);
      setEditing(false);
      if (onSaved) onSaved(res.data.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setDraft(state.mode || DEFAULT_RELANCE_MODE);
    setEditing(false);
    setError(null);
  };

  const anchorLabel = state.effective_mode === 'reception' ? 'Réception' : 'Facturation';

  return (
    <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <FontAwesomeIcon icon={faMoneyBillWave} size="xs" style={{ color: accent }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 700, color: titleColor, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Délai de relance paiement
        </span>
        {saving && <FontAwesomeIcon icon={faSpinner} size="xs" spin style={{ color: accent }} />}
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', fontSize: '0.7rem', padding: '6px 8px', borderRadius: 6, marginBottom: 6 }}>
          {error}
        </div>
      )}

      {editing ? (
        <>
          <select
            value={draft}
            disabled={saving}
            onChange={e => setDraft(e.target.value)}
            style={{
              width: '100%', fontSize: '0.76rem', padding: '6px 8px', border: '1px solid #e5e7eb',
              borderRadius: 6, outline: 'none', color: '#374151', background: '#fff',
              cursor: saving ? 'default' : 'pointer', marginBottom: 8,
            }}
          >
            {RELANCE_MODES.map(m => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            <button onClick={handleCancel} disabled={saving}
              style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer' }}>
              Annuler
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', background: '#1d4ed8', color: '#fff', fontSize: '0.74rem', fontWeight: 600, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.6 : 1 }}>
              {saving ? <FontAwesomeIcon icon={faSpinner} size="xs" spin /> : 'Enregistrer'}
            </button>
          </div>
        </>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>
            {state.label}
            {state.is_default && <span style={{ color: '#9ca3af', fontWeight: 400 }}> (par défaut)</span>}
          </span>
          {canEdit && (
            <button onClick={() => { setDraft(state.mode || DEFAULT_RELANCE_MODE); setEditing(true); }}
              style={{ fontSize: '0.7rem', padding: '3px 10px', borderRadius: 6, border: `1px solid ${accent}`, background: '#fff', color: accent, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
              <FontAwesomeIcon icon={faEdit} size="xs" /> Modifier
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#4b5563', marginBottom: 3 }}>
        <span>{anchorLabel}</span>
        <span style={{ fontWeight: 600 }}>{fmt(state.anchor_at)}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#4b5563', marginBottom: 3 }}>
        <span>Échéance</span>
        <span style={{ fontWeight: 600, color: overdue ? '#dc2626' : '#4b5563' }}>
          {fmt(state.due_date)}
          {overdue && ` · dépassée de ${state.days_overdue} j`}
        </span>
      </div>

      {state.next_action && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#4b5563' }}>
            <span>
              {(state.next_action_late ? LATE_ACTION_LABELS : NEXT_ACTION_LABELS)[state.next_action]}
            </span>
            <span style={{ fontWeight: 600, color: state.next_action_late ? '#dc2626' : '#4b5563' }}>
              {fmt(state.next_action_at)}
            </span>
          </div>
          {state.next_action_late && (
            <div style={{ fontSize: '0.66rem', color: '#6b7280', marginTop: 4 }}>
              Pas encore envoyée : part au prochain passage (jours ouvrés, 09:00).
            </div>
          )}
        </>
      )}

      {(state.relance_sent_at || state.escalation_count > 0) && (
        <div style={{ fontSize: '0.66rem', color: '#6b7280', marginTop: 6, paddingTop: 6, borderTop: `1px solid ${border}` }}>
          {state.relance_sent_at && <>Relance envoyée le {fmt(state.relance_sent_at)}</>}
          {state.relance2_sent_at && <> · 2e le {fmt(state.relance2_sent_at)}</>}
          {state.escalation_count > 0 && (
            <> · {state.escalation_count} escalade{state.escalation_count > 1 ? 's' : ''} à la direction</>
          )}
        </div>
      )}

      {state.last_changed_at && (
        <div style={{ fontSize: '0.64rem', color: '#9ca3af', marginTop: 6 }}>
          Modifié le {new Date(state.last_changed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
          {state.last_changed_by ? ` par ${state.last_changed_by}` : ''}
        </div>
      )}
    </div>
  );
};

export default PaymentRelanceCard;
